/* vr-product-carousel.js — arrows + counter for sections/vr-product-carousel.liquid.
   Vanilla ES6, no jQuery, and no data-section-type: atlantic.js never sees this section.

   Scroll model (docs/FIGMA-REFERENCE.md "Slide behaviour"): the track is a plain overflow
   scroller whose 60/16 gutters are its own padding. Advancing scrolls by exactly one card, so
   the first step turns the left gutter into a cut card and the browser's own clamp at the far
   end restores the gutter on the right. No scroll-snap — snapping would put the gutter back at
   every stop. */
(function () {
  'use strict';

  var EPS = 2; /* sub-pixel slack: scrollLeft is fractional on zoomed/HiDPI displays */
  var DRAG_MIN = 5; /* px of movement before a press becomes a drag rather than a click */

  function preventDefault(e) { e.preventDefault(); }

  function Carousel(root) {
    this.root = root;
    this.track = root.querySelector('[data-vr-carousel-track]');
    this.pagination = root.querySelector('[data-vr-carousel-pagination]');
    this.prev = root.querySelector('[data-vr-carousel-prev]');
    this.next = root.querySelector('[data-vr-carousel-next]');
    this.counter = root.querySelector('[data-vr-carousel-counter]');
    this.cells = Array.prototype.slice.call(root.querySelectorAll('[data-vr-carousel-cell]'));
    this.ticking = false;

    if (!this.track || !this.cells.length) return;

    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    if (this.prev) this.prev.addEventListener('click', this.go.bind(this, -1));
    if (this.next) this.next.addEventListener('click', this.go.bind(this, 1));

    /* Mouse drag scrolls the track (client, 2026-09-14). Touch and pen keep the browser's own
       scrolling. Only a real drag swallows the click that ends it, so a plain click still opens
       the card or the Instagram post. */
    this.drag = null;
    this.suppressClick = false;
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClick = this.onClick.bind(this);
    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('click', this.onClick, true);
    this.track.addEventListener('dragstart', preventDefault);

    /* Trackpads and touch scroll the track without ever touching an arrow, so the counter has to
       follow the scroll position, not the clicks. */
    if (typeof ResizeObserver === 'function') {
      this.ro = new ResizeObserver(this.update);
      this.ro.observe(this.track);
    } else {
      window.addEventListener('resize', this.update);
    }

    this.update();
    root.__vrCarousel = this;
  }

  /* One card + one gap, measured rather than assumed, so the mobile card width needs no
     matching constant here. */
  Carousel.prototype.step = function () {
    if (this.cells.length > 1) {
      var d = this.cells[1].offsetLeft - this.cells[0].offsetLeft;
      if (d > 0) return d;
    }
    return this.cells[0].getBoundingClientRect().width;
  };

  Carousel.prototype.max = function () {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  };

  Carousel.prototype.go = function (dir) {
    this.track.scrollBy({ left: this.step() * dir, behavior: this.behavior() });
  };

  Carousel.prototype.behavior = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  };

  Carousel.prototype.onScroll = function () {
    if (this.ticking) return;
    this.ticking = true;
    var self = this;
    window.requestAnimationFrame(function () {
      self.ticking = false;
      self.update();
    });
  };

  Carousel.prototype.update = function () {
    var max = this.max();
    var x = this.track.scrollLeft;

    /* Nothing to scroll: the arrows would be two dead controls, so the whole row goes. */
    if (this.pagination) this.pagination.hidden = max <= EPS;
    if (max <= EPS) return;

    if (this.prev) this.prev.disabled = x <= EPS;
    if (this.next) this.next.disabled = x >= max - EPS;

    if (this.counter) {
      /* Rouje-style (client, 2026-09-17): the number is the FIRST card fully on screen, and the
         total is that same number at the far end — so every arrow click adds one and the last
         click reads total/total. 7 cards with 3 in view: 1/5 … 5/5; one card in view: 1/7 … 7/7.
         Both come from the row's geometry, not from clicks, so a free swipe or drag still reads
         true (no snapping), and the total is worked out for the end position rather than from
         whatever is visible now, so it cannot change mid-drag. */
      var box = this.track.getBoundingClientRect();
      var cw = this.track.clientWidth;
      var n = this.cells.length;
      var first = -1;
      var best = 0;
      var bestCover = -Infinity;
      var total = -1;
      for (var k = 0; k < n; k++) {
        var r = this.cells[k].getBoundingClientRect();
        if (first < 0 && r.left >= box.left - EPS && r.right <= box.right + EPS) first = k;
        var cover = Math.min(r.right, box.right) - Math.max(r.left, box.left);
        if (cover > bestCover) { bestCover = cover; best = k; }
        /* This card's box in the row's own coordinates, then where it sits once scrolled to max. */
        var atEndLeft = r.left - box.left + x - max;
        if (total < 0 && atEndLeft >= -EPS && atEndLeft + r.width <= cw + EPS) total = k + 1;
      }
      if (first < 0) first = best;
      if (total < 0) total = n;
      var current = Math.min(first + 1, total);
      var label = current + '/' + total;
      /* Reserve the widest label ("10/11" is wider than "1/11"), so the arrows never move. */
      if (this.counterWidthFor !== total) {
        this.counter.style.minWidth = '';
        this.counter.textContent = total + '/' + total;
        this.counter.style.minWidth = Math.ceil(this.counter.getBoundingClientRect().width) + 'px';
        this.counter.style.textAlign = 'center';
        this.counterWidthFor = total;
      }
      if (this.counter.textContent !== label) this.counter.textContent = label;
    }
  };

  Carousel.prototype.onPointerDown = function (e) {
    if (e.pointerType !== 'mouse' || e.button !== 0 || this.max() <= EPS) return;
    this.drag = { id: e.pointerId, x: e.clientX, left: this.track.scrollLeft, moved: false };
    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    this.track.addEventListener('pointercancel', this.onPointerUp);
  };

  Carousel.prototype.onPointerMove = function (e) {
    var d = this.drag;
    if (!d || e.pointerId !== d.id) return;
    var dx = e.clientX - d.x;
    if (!d.moved) {
      if (Math.abs(dx) < DRAG_MIN) return;
      d.moved = true;
      /* Captured only once it is a drag: capturing on pointerdown would retarget a plain click
         to the track and break the card links. */
      this.track.setPointerCapture(d.id);
      this.track.classList.add('is-dragging');
    }
    this.track.scrollLeft = d.left - dx;
  };

  Carousel.prototype.onPointerUp = function (e) {
    var d = this.drag;
    if (!d || e.pointerId !== d.id) return;
    this.endDrag();
    if (!d.moved) return;
    /* The click that follows this pointerup is dispatched before any timer runs, so the flag
       only ever eats that one click, even when the button was released off the track. */
    var self = this;
    this.suppressClick = true;
    window.setTimeout(function () { self.suppressClick = false; }, 0);
  };

  Carousel.prototype.endDrag = function () {
    if (this.drag && this.track.hasPointerCapture(this.drag.id)) {
      this.track.releasePointerCapture(this.drag.id);
    }
    this.drag = null;
    this.track.classList.remove('is-dragging');
    this.track.removeEventListener('pointermove', this.onPointerMove);
    this.track.removeEventListener('pointerup', this.onPointerUp);
    this.track.removeEventListener('pointercancel', this.onPointerUp);
  };

  /* Capture phase on the track, so it runs before the card link or the app's popup handler. */
  Carousel.prototype.onClick = function (e) {
    if (!this.suppressClick) return;
    this.suppressClick = false;
    e.preventDefault();
    e.stopPropagation();
  };

  Carousel.prototype.destroy = function () {
    this.endDrag();
    this.track.removeEventListener('pointerdown', this.onPointerDown);
    this.track.removeEventListener('click', this.onClick, true);
    this.track.removeEventListener('dragstart', preventDefault);
    this.track.removeEventListener('scroll', this.onScroll);
    if (this.ro) this.ro.disconnect();
    else window.removeEventListener('resize', this.update);
    delete this.root.__vrCarousel;
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-vr-carousel]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__vrCarousel) root.__vrCarousel.destroy();
      new Carousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Other sections whose cells are not rendered by Liquid (vr-social-feed.js: the Instagram
     app’s posts) re-run the hand-off after their own render. */
  window.vrCarousel = { init: init };

  /* Theme editor: the section is replaced wholesale on every save. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-vr-carousel]');
    if (root && root.__vrCarousel) root.__vrCarousel.destroy();
  });
})();
